import React from 'react'

/**
 * CallLogsTable Component
 * Follows the AttendanceHistoryTable design standard.
 * Renders call audit records with direction indicators, agent assignments, timestamp, status pills,
 * ERP job link, inline WebRTC recording audio player, and detail inspection action.
 */
export default function CallLogsTable({
  filteredLogs = [],
  playingAudioId,
  onToggleAudio,
  onSelectLog,
  formatDuration,
  loading = false,
}) {
  if (loading) {
    return (
      <div className="flex h-64 w-full items-center justify-center rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-xs">
        <div className="flex flex-col items-center gap-2">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <span className="text-xs font-semibold text-on-surface-variant">
            Loading call audit records...
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-xs">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-outline-variant/25 bg-surface-container-low/40 text-[11px] font-extrabold uppercase tracking-wider text-black">
              <th className="py-3.5 px-5 text-black">Direction &amp; Contact</th>
              <th className="py-3.5 px-5 text-black">Assigned Agent</th>
              <th className="py-3.5 px-5 text-black">Timestamp &amp; Duration</th>
              <th className="py-3.5 px-5 text-black">Call Status</th>
              <th className="py-3.5 px-5 text-black">ERP Job Link</th>
              <th className="py-3.5 px-5 text-center text-black">Audio Recording</th>
              <th className="py-3.5 px-5 text-right text-black">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/15 text-xs">
            {filteredLogs.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <span className="material-symbols-outlined mb-2 text-4xl text-outline-variant">
                      history_toggle_off
                    </span>
                    <h3 className="font-display text-sm font-bold text-on-surface">
                      No Call Logs Found
                    </h3>
                    <p className="mt-1 text-xs text-on-surface-variant max-w-sm">
                      No call records matched your selected filters or search query.
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredLogs.map((log) => (
                <tr
                  key={log.id}
                  className="hover:bg-surface-container-low/40 transition-colors group cursor-pointer"
                  onClick={() => onSelectLog(log)}
                >
                  {/* Direction & Contact */}
                  <td className="py-3.5 px-5">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                          log.direction === 'INBOUND'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-blue-50 text-blue-700 border border-blue-200'
                        }`}
                      >
                        <span className="material-symbols-outlined text-base">
                          {log.direction === 'INBOUND' ? 'south_west' : 'north_east'}
                        </span>
                      </div>
                      <div>
                        <div className="font-bold text-on-surface flex items-center gap-2">
                          <span>{log.caller_name || log.customer_name || 'Customer'}</span>
                          <span className="px-1.5 py-0.5 rounded-md bg-surface-container-high text-[10px] font-extrabold text-on-surface-variant">
                            {log.direction}
                          </span>
                        </div>
                        <p className="text-[11px] text-on-surface-variant font-mono">
                          {log.caller_number || log.called_number}
                        </p>
                        {log.project_name && (
                          <p className="text-[10px] font-bold text-primary truncate max-w-[200px]">
                            {log.project_name}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Assigned Agent */}
                  <td className="py-3.5 px-5">
                    <div className="font-bold text-on-surface">{log.agent_name || 'System Auto'}</div>
                    {log.agent_extension && (
                      <div className="text-[11px] text-on-surface-variant font-medium">
                        Ext #{log.agent_extension}
                      </div>
                    )}
                  </td>

                  {/* Timestamp & Duration */}
                  <td className="py-3.5 px-5">
                    <div className="font-semibold text-on-surface">
                      {log.start_time
                        ? new Date(log.start_time).toLocaleString([], {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '—'}
                    </div>
                    <span className="inline-block px-2 py-0.5 mt-0.5 rounded-full bg-surface-container-high text-[11px] font-extrabold text-on-surface-variant">
                      {formatDuration(log.duration_seconds)}
                    </span>
                  </td>

                  {/* Call Status */}
                  <td className="py-3.5 px-5">
                    {(() => {
                      const st = (log.call_status || '').toUpperCase()

                      // 1. Completed: Green
                      if (st === 'COMPLETED') {
                        return (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-[11px] font-bold tracking-tight shadow-xs">
                            <span className="material-symbols-outlined text-xs leading-none text-emerald-600">check_circle</span>
                            <span>Completed</span>
                          </span>
                        )
                      }

                      // 2. Missed Call: Rose
                      if (st === 'MISSED') {
                        return (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-50 text-rose-800 border border-rose-200 text-[11px] font-bold tracking-tight shadow-xs">
                            <span className="material-symbols-outlined text-xs leading-none text-rose-600">phone_missed</span>
                            <span>Missed Call</span>
                          </span>
                        )
                      }

                      // 3. Agent Declined: Orange / Amber
                      if (st === 'BUSY') {
                        return (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-900 border border-amber-300 text-[11px] font-bold tracking-tight shadow-xs">
                            <span className="material-symbols-outlined text-xs leading-none text-amber-700">phone_disabled</span>
                            <span>{log.direction === 'INBOUND' ? 'Agent Declined' : 'Busy / Declined'}</span>
                          </span>
                        )
                      }

                      // 4. Dropped / Abandoned: Slate / Gray
                      if (['CANCELED', 'CANCELLED'].includes(st)) {
                        return (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-300 text-[11px] font-bold tracking-tight shadow-xs">
                            <span className="material-symbols-outlined text-xs leading-none text-slate-500">call_end</span>
                            <span>Dropped / Abandoned</span>
                          </span>
                        )
                      }

                      // 5. No Answer: Yellow
                      if (['NO-ANSWER', 'NO_ANSWER'].includes(st)) {
                        return (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-50 text-yellow-800 border border-yellow-300 text-[11px] font-bold tracking-tight shadow-xs">
                            <span className="material-symbols-outlined text-xs leading-none text-yellow-600">phone_missed</span>
                            <span>No Answer</span>
                          </span>
                        )
                      }

                      // 6. Failed: Red
                      if (st === 'FAILED') {
                        return (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 text-red-700 border border-red-200 text-[11px] font-bold tracking-tight shadow-xs">
                            <span className="material-symbols-outlined text-xs leading-none text-red-600">error</span>
                            <span>Failed</span>
                          </span>
                        )
                      }

                      // Active / Ringing (live)
                      if (['ACTIVE', 'IN-PROGRESS', 'IN_PROGRESS', 'RINGING'].includes(st)) {
                        return (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 text-blue-800 border border-blue-200 text-[11px] font-bold tracking-tight shadow-xs animate-pulse">
                            <span className="material-symbols-outlined text-xs leading-none text-blue-600">ring_volume</span>
                            <span>Active</span>
                          </span>
                        )
                      }

                      // Fallback
                      return (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-surface-container-high text-on-surface-variant border border-outline-variant/30 text-[11px] font-semibold tracking-tight">
                          {log.call_status || 'Unknown'}
                        </span>
                      )
                    })()}
                  </td>

                  {/* ERP Job / Timesheet Link */}
                  <td className="py-3.5 px-5">
                    {log.timesheet_id ? (
                      <div>
                        <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-800 font-bold text-[11px] border border-blue-200 block w-max">
                          TS #{log.timesheet_id}
                        </span>
                        <span className="text-[10px] font-bold text-amber-700 block mt-0.5">
                          {log.timesheet_status || 'PENDING'}
                        </span>
                      </div>
                    ) : log.job_id ? (
                      <div>
                        <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-800 font-bold text-[11px] border border-blue-200 block w-max">
                          Job #{log.job_id}
                        </span>
                        <span className="text-[10px] font-semibold text-amber-600 block mt-0.5">
                          {log.job_status}
                        </span>
                      </div>
                    ) : (
                      <span className="text-[11px] text-on-surface-variant/70 italic">
                        No Timesheet
                      </span>
                    )}
                  </td>

                  {/* Audio Recording */}
                  <td className="py-3.5 px-5 text-center" onClick={(e) => e.stopPropagation()}>
                    {log.recording_url ? (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => onToggleAudio(log.id)}
                          className={`w-8 h-8 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                            playingAudioId === log.id
                              ? 'bg-primary text-on-primary shadow-sm ring-2 ring-primary/20'
                              : 'bg-surface-container-high hover:bg-surface-container-highest text-on-surface'
                          }`}
                          title={playingAudioId === log.id ? 'Pause Audio' : 'Play Voice Recording'}
                        >
                          <span className="material-symbols-outlined text-base">
                            {playingAudioId === log.id ? 'pause' : 'play_arrow'}
                          </span>
                        </button>

                        {playingAudioId === log.id && (
                          <div className="flex items-center gap-1">
                            <span className="w-1 h-3 bg-primary animate-pulse" />
                            <span className="w-1 h-4 bg-primary animate-pulse delay-75" />
                            <span className="w-1 h-2 bg-primary animate-pulse delay-150" />
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-[11px] text-on-surface-variant/60 italic">
                        No Recording
                      </span>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="py-3.5 px-5 text-right" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => onSelectLog(log)}
                      className="px-3 py-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-lowest hover:bg-surface-container-low text-xs font-bold text-primary hover:text-primary/80 transition-colors cursor-pointer"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
